import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react';

const DataIngestionModal = ({ isOpen, onClose, onSubmit, title, description }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', null
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['xlsx', 'xls', 'csv'];

      if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
        setSelectedFile(file);
        setUploadStatus(null);
        setUploadResult(null);
      } else {
        alert('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file');
        event.target.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setUploadResult(null);

    try {
      const result = await onSubmit(selectedFile, scheduleTime || null);
      setUploadStatus('success');
      setUploadResult(result);
    } catch (error) {
      setUploadStatus('error');
      setUploadResult({ error: error.message || 'Upload failed' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setScheduleTime('');
      setUploadStatus(null);
      setUploadResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderUploadResult = () => {
    if (!uploadResult) return null;

    if (uploadStatus === 'success') {
      const isScheduled = uploadResult.status === 'scheduled';
      const hasNoValidRecords = !isScheduled && 
                                 (uploadResult.records_valid === 0 || uploadResult.records_loaded === 0) &&
                                 uploadResult.records_extracted > 0;
      
      // Show warning if file was processed but no records were loaded
      if (hasNoValidRecords) {
        return (
          <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                  Upload Processed - No Valid Records Found
                </h4>
                <p className="text-xs text-yellow-800 mb-3">
                  The file was uploaded but no records could be validated and loaded into the database.
                </p>
                
                <div className="space-y-2 text-sm text-gray-700 mb-3">
                  <div className="flex justify-between">
                    <span className="font-medium">Filename:</span>
                    <span className="text-xs">{uploadResult.filename}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Records Extracted:</span>
                    <span className="font-semibold">{uploadResult.records_extracted || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-yellow-700">Records Valid:</span>
                    <span className="font-semibold text-yellow-700">{uploadResult.records_valid || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-red-700">Records Loaded:</span>
                    <span className="font-semibold text-red-700">{uploadResult.records_loaded || 0}</span>
                  </div>
                </div>

                <div className="p-3 bg-yellow-100 border border-yellow-300 rounded mb-3">
                  <p className="text-xs font-semibold text-yellow-900 mb-2">Common Issues:</p>
                  <ul className="text-xs text-yellow-900 space-y-1">
                    <li>• <strong>Missing "Amplify Campaign Name" column</strong> - This column is required</li>
                    <li>• <strong>Campaign names don't match</strong> - Names must match existing campaigns</li>
                    <li>• <strong>Invalid data formats</strong> - Check date formats, numeric values, etc.</li>
                    <li>• <strong>Column name mismatch</strong> - Verify column headers match expected names</li>
                    <li>• <strong>Empty or invalid rows</strong> - Remove blank rows and check data quality</li>
                  </ul>
                </div>

                {uploadResult.error_message && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded mb-3">
                    <p className="text-xs font-semibold text-red-900 mb-1">Error Message:</p>
                    <p className="text-xs text-red-800 whitespace-pre-wrap">{uploadResult.error_message}</p>
                  </div>
                )}

                {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded mb-3">
                    <p className="text-xs font-semibold text-orange-900 mb-1">Warnings:</p>
                    <ul className="text-xs text-orange-800 space-y-1">
                      {uploadResult.warnings.map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      setUploadStatus(null);
                      setUploadResult(null);
                      setSelectedFile(null);
                    }}
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium text-sm"
                  >
                    Try Different File
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-green-900 mb-2">
                {isScheduled ? 'Upload Scheduled Successfully!' : 'Upload Completed Successfully!'}
              </h4>
              
              <div className="space-y-2 text-sm text-green-800">
                <div className="flex justify-between">
                  <span className="font-medium">Filename:</span>
                  <span>{uploadResult.filename}</span>
                </div>
                
                {!isScheduled && (
                  <>
                    <div className="flex justify-between">
                      <span className="font-medium">Records Extracted:</span>
                      <span className="font-semibold">{uploadResult.records_extracted}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Records Valid:</span>
                      <span className="font-semibold text-green-700">{uploadResult.records_valid}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Records Loaded:</span>
                      <span className="font-semibold text-green-700">{uploadResult.records_loaded}</span>
                    </div>
                    {uploadResult.records_invalid > 0 && (
                      <div className="flex justify-between">
                        <span className="font-medium">Records Invalid:</span>
                        <span className="font-semibold text-orange-600">{uploadResult.records_invalid}</span>
                      </div>
                    )}
                    {uploadResult.duration_seconds && (
                      <div className="flex justify-between">
                        <span className="font-medium">Duration:</span>
                        <span>{uploadResult.duration_seconds.toFixed(2)}s</span>
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex justify-between">
                  <span className="font-medium">Ingestion Time:</span>
                  <span>{new Date(uploadResult.ingestion_time).toLocaleString()}</span>
                </div>
                
                {uploadResult.ingested_by && (
                  <div className="flex justify-between">
                    <span className="font-medium">Ingested By:</span>
                    <span>{uploadResult.ingested_by}</span>
                  </div>
                )}
              </div>
              
              {uploadResult.warnings && uploadResult.warnings.length > 0 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs font-semibold text-yellow-900 mb-1">Warnings:</p>
                  <ul className="text-xs text-yellow-800 list-disc list-inside space-y-1">
                    {uploadResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <button
                onClick={handleClose}
                className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (uploadStatus === 'error') {
      return (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-2">Upload Failed</h4>
              <p className="text-sm text-red-800">{uploadResult.error}</p>
              <button
                onClick={() => {
                  setUploadStatus(null);
                  setUploadResult(null);
                }}
                className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Upload className="h-5 w-5 mr-2" />
                {title}
              </h3>
              {!isUploading && (
                <button
                  onClick={handleClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <p className="text-sm text-blue-100 mt-1">{description}</p>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {!uploadStatus && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* File Upload */}
                <div>
                  <label htmlFor="data-ingestion-file" className="block text-sm font-medium text-gray-700 mb-2">
                    Select File <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      id="data-ingestion-file"
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={isUploading}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {selectedFile ? selectedFile.name : 'Click to browse files'}
                      </span>
                    </button>
                  </div>
                  {selectedFile && (
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{formatBytes(selectedFile.size)}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Supported formats: Excel (.xlsx, .xls) or CSV (.csv)
                  </p>
                </div>

                {/* Schedule Time (Optional) */}
                <div>
                  <label htmlFor="data-ingestion-schedule" className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Time (Optional)
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="data-ingestion-schedule"
                      type="datetime-local"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      disabled={isUploading}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty for immediate ingestion
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isUploading}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || isUploading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Upload Result */}
            {renderUploadResult()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataIngestionModal;
